import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ITranslations } from '../data/strings';
import IFamily from '../models/IFamily';
import { ELCID, IMediaPlanPage } from '../models/IMediaToolkit';

export interface INavButtonProps {
    family: IFamily;
    type: 'Back' | 'Forward';
    arrow: JSX.Element;
    pages: IMediaPlanPage[];
    variant: 'floating' | 'mobile';
    lcid: ELCID;
    translations: ITranslations;
    className?: string;
    topPosition?: number;
}

export const NavButton: React.FunctionComponent<INavButtonProps> = (props) => {
    const location = useLocation();
    const navigate = useNavigate();

    let buttonDisabled = false;
    let buttonClasses = `w-40 h-40 absolute ${props.type === 'Back' ? `left-0` : `right-0`} top-10 rounded-full`;
    let textClasses = `absolute ${props.type === 'Back' ? `left-0` : `right-0`} top-1/2 w-1/2 -translate-y-7 `;
    let buttonStyles: React.CSSProperties = {
        top: `auto`,
    };

    if (props.variant === 'mobile') {
        buttonClasses = `px-3 py-2 rounded-md`;
        textClasses = ``;
    } else {
        buttonStyles.top = `${props.topPosition}px`;
    }

    let curPageIdx = -1;

    const validPages = props.pages.filter((page) => (!page.availablePriority && !page.dontCountAsStep) || props.family.priorities.indexOf(page.spoId) > -1);
    let nextValidPageIdx: number = 0;
    let prevValidPageIdx: number = 0;

    let prevPageCounterLabel: number = -1;
    let nextPageCounterLabel: number = -1;

    // get current page index from main pages array
    for (let i = 0; i < props.pages.length; i++) {
        if (location.pathname === props.pages[i].navigateTo) {
            curPageIdx = i;
            break;
        }
    }

    // get next valid page info
    for (let i = curPageIdx + 1; i < props.pages.length; i++) {
        if (props.family.priorities.indexOf(props.pages[i].spoId) > -1 || (!props.pages[i].availablePriority && !props.pages[i].dontCountAsStep)) {
            nextValidPageIdx = i;
            for (let j = 0; j < validPages.length; j++) {
                if (validPages[j].identifier === props.pages[i].identifier) {
                    nextPageCounterLabel = j + 1;
                }
            }
            break;
        }
    }

    // get previous valid page info
    for (let i = curPageIdx - 1; i >= 0; i--) {
        if (props.family.priorities.indexOf(props.pages[i].spoId) > -1 || (!props.pages[i].availablePriority && !props.pages[i].dontCountAsStep)) {
            prevValidPageIdx = i;
            for (let j = 0; j < validPages.length; j++) {
                if (validPages[j].identifier === props.pages[i].identifier) {
                    prevPageCounterLabel = j + 1;
                }
            }
            break;
        }
    }

    // if on family page, disable forward if not enough family information is filled out
    if(props.type === 'Forward' && location.pathname === '/family' && (!props.family.name || props.family.members.filter(member => !member.name).length > 0)) {
        buttonDisabled = true;
    }

    if ((props.type === 'Back' && curPageIdx === 0) || (props.type === 'Forward' && curPageIdx >= props.pages.length - 1) || curPageIdx === -1) {
        buttonClasses += ` opacity-0 pointer-events-none`;
    }

    return (
        <button
            type="button"
            style={buttonStyles}
            className={`print:hidden bg-aap-green-500 text-white transition-colors hover:bg-aap-green-700 duration-500 ${buttonClasses} ${props.className} z-10 disabled:bg-aap-gray-400 disabled:hover:bg-aap-gray-400 disabled:text-opacity-50`}
            id={`btn_backFwdBtn_${props.type}_${ELCID[props.lcid]}`}
            disabled={buttonDisabled}
            onClick={() => {
                if (props.type === 'Back') {
                    navigate(props.pages[prevValidPageIdx].navigateTo);
                } else {
                    navigate(props.pages[nextValidPageIdx].navigateTo);
                }
            }}
        >
            <div className={`${textClasses} text-sm`}>
                {props.variant === 'mobile' && props.type === 'Back' ? props.arrow : null}
                <div className={`font-semibold ${props.variant === 'mobile' ? `inline` : ``}`}>{props.type === 'Back' ? `${props.translations.back} ` : `${props.translations.continue} `}</div>
                {(props.type === 'Back' && prevPageCounterLabel > -1) || (props.type === 'Forward' && nextPageCounterLabel > -1) ? (
                    <div className={`text-xs ${props.variant === 'mobile' ? `inline` : ``}`}>
                        {props.translations.xOfy.replace('{{x}}', props.type === 'Back' ? prevPageCounterLabel.toString() : nextPageCounterLabel.toString()).replace('{{y}}', validPages.length.toString())}
                    </div>
                ) : null}
                {(props.variant === 'mobile' && props.type === 'Forward') || props.variant === 'floating' ? props.arrow : null}
            </div>
        </button>
    );
};
